import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import axios from 'axios';
import { apiClient } from '../api/client';
import type { StaffRole } from '../constants/staff';

type AuthUser = Awaited<ReturnType<typeof getCurrentUser>>;

/** GET /api/me の職員プロフィール */
export type StaffProfile = {
  id: number;
  email: string;
  display_name: string;
  role: StaffRole;
  avatar_url: string | null;
  /** 開発者用アカウント（院内データの多くが API でマスクされる） */
  is_developer: boolean;
  /** google: Google OAuth。email_password: メール＋パスワード（招待） */
  auth_method: 'google' | 'email_password';
};

export type StaffProfileError = 'forbidden' | 'other';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  refreshUser: () => Promise<void>;
  signOutUser: () => Promise<void>;
  staffProfile: StaffProfile | null;
  staffProfileLoading: boolean;
  staffProfileError: StaffProfileError | null;
  refreshStaffProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [staffProfile, setStaffProfile] = useState<StaffProfile | null>(null);
  const [staffProfileLoading, setStaffProfileLoading] = useState(false);
  const [staffProfileError, setStaffProfileError] =
    useState<StaffProfileError | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const u = await getCurrentUser();
      setUser(u);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  useEffect(() => {
    if (!user) {
      setStaffProfile(null);
      setStaffProfileError(null);
      setStaffProfileLoading(false);
      return;
    }

    let cancelled = false;
    setStaffProfileLoading(true);
    setStaffProfileError(null);

    apiClient
      .get<StaffProfile>('/api/me')
      .then((res) => {
        if (!cancelled) {
          setStaffProfile(res.data);
          setStaffProfileError(null);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setStaffProfile(null);
        if (axios.isAxiosError(e) && e.response?.status === 403) {
          setStaffProfileError('forbidden');
        } else {
          setStaffProfileError('other');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStaffProfileLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const refreshStaffProfile = useCallback(async () => {
    if (!user) return;
    setStaffProfileLoading(true);
    setStaffProfileError(null);
    try {
      const res = await apiClient.get<StaffProfile>('/api/me');
      setStaffProfile(res.data);
    } catch (e: unknown) {
      setStaffProfile(null);
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        setStaffProfileError('forbidden');
      } else {
        setStaffProfileError('other');
      }
    } finally {
      setStaffProfileLoading(false);
    }
  }, [user]);

  const signOutUser = useCallback(async () => {
    const redirectUrl = process.env.REACT_APP_OAUTH_REDIRECT_SIGN_OUT;
    setStaffProfile(null);
    setStaffProfileError(null);
    try {
      if (redirectUrl) {
        await signOut({ global: true, oauth: { redirectUrl } });
      } else {
        await signOut({ global: true });
      }
      setUser(null);
    } catch {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      refreshUser,
      signOutUser,
      staffProfile,
      staffProfileLoading,
      staffProfileError,
      refreshStaffProfile,
    }),
    [
      user,
      isLoading,
      refreshUser,
      signOutUser,
      staffProfile,
      staffProfileLoading,
      staffProfileError,
      refreshStaffProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth は AuthProvider 内で使ってください');
  }
  return ctx;
}
